import {
  ModalConfirmV2,
  ModalSizeVariants,
  MultiSelectV4,
  TableComponentV2,
} from '@labrav/react-components'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useStyles } from './MapCrmFields.styles'
import { faLineColumns } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TableCell, Typography } from '@mui/material'
import {
  camelCaseToWords,
  isCrmPicklistFieldType,
  isSelectedCrmPicklistField as resolveIsSelectedCrmPicklistField,
  MapTablePicklistMapping,
} from '../utils/mappingManagerConstants'
import { useTracker, useUserType } from '@labrav/common'
import { snakeize } from 'casing'
import { useSelector } from '../../../store'
import type { UserProfile } from '@labrav/common'
import { customerFormData } from '../utils/customerFormData'
import { CRMTypes } from '../../../utils/constants'
import { useFlagValue } from '@labrav/flags'
import type {
  MarketplaceFieldMappingPicklistData,
  MarketplaceHubspotFieldsData,
  MarketplaceHubspotFieldsOptions,
} from '../../../modules/marketplaceMappingManager/types'

const MapCrmFieldsCustomHeaderCell: React.FC<{
  label: string
  className?: string
}> = ({ label, className }) => (
  <TableCell className={className}>
    <Typography>{label}</Typography>
  </TableCell>
)

interface HubspotValuesMultiSelectProps {
  picklistMappings: MarketplaceFieldMappingPicklistData[]
  referralValue: string
  onSelectionChange: (mappings: MarketplaceFieldMappingPicklistData[]) => void
  allOptions: MarketplaceHubspotFieldsOptions[]
  allSelectedValues: string[]
}

const HubspotValuesMultiSelect = React.memo(
  ({
    picklistMappings = [],
    referralValue,
    onSelectionChange,
    allOptions,
    allSelectedValues,
  }: HubspotValuesMultiSelectProps) => {
    const initialSelectedValues = useMemo(() => {
      return picklistMappings
        .filter(m => m.referralValue === referralValue)
        .map(m => m.hubspotValue)
    }, [picklistMappings, referralValue])

    const [selectedValues, setSelectedValues] = useState<string[]>(
      initialSelectedValues
    )
    const availableOptions = useMemo(() => {
      return allOptions.filter(
        option =>
          !allSelectedValues.includes(option.value) ||
          selectedValues.includes(option.value)
      )
    }, [allOptions, allSelectedValues, selectedValues])

    const handleSelect = useCallback(
      (event: unknown) => {
        const newSelectedValues: any = (
          event as React.ChangeEvent<HTMLInputElement>
        ).target.value

        const updatedMappings = newSelectedValues.map((value: string) => ({
          referralValue,
          hubspotValue: value,
          hubspotLabel:
            allOptions?.find(o => o.value === value)?.label || value,
        }))

        setSelectedValues(newSelectedValues)
        onSelectionChange(updatedMappings)
      },
      [allOptions, onSelectionChange, referralValue]
    )

    const handleDelete = useCallback(
      (value: string) => {
        const newValues = selectedValues.filter(v => v !== value)
        setSelectedValues(newValues)
        onSelectionChange(
          newValues.map(v => ({
            referralValue,
            hubspotValue: v,
            hubspotLabel: allOptions?.find(o => o.value === v)?.label || v,
          }))
        )
      },
      [allOptions, onSelectionChange, referralValue, selectedValues]
    )

    return (
      <div>
        <MultiSelectV4
          field={{
            title: 'Select an option',
            placeHolder: 'Select an option',
            humanReadableKey: 'picklistMappings',
            id: 'picklistMappings',
            isRequired: true,
            isReadOnly: false,
            isCustom: false,
            dataType: 'multi_enum',
            options: availableOptions,
          }}
          onChangeSelect={handleSelect}
          selectedValues={selectedValues}
          handleDelete={handleDelete}
          chipDisplayLimit={1}
        />
      </div>
    )
  }
)

interface MapHubspotFieldsProps {
  hubspotField: string
  hubspotObject: string
  hubspotFieldData?: any[]
  category?: string
  fieldKey: string
  picklistMappings?: any[]
  openModal: boolean
  setOpenModal?: React.Dispatch<React.SetStateAction<boolean>>
  picklist?: any[]
  context?: 'cosell' | 'marketplace'
  updateAction?: (
    category: string | undefined,
    fieldKey: string,
    data: { picklistMappings: any[] }
  ) => void
}

const EMPTY_HUBSPOT_FIELD_DATA: MarketplaceHubspotFieldsData[] = []
const EMPTY_PICKLIST_MAPPINGS: MarketplaceFieldMappingPicklistData[] = []
const EMPTY_PICKLIST: MarketplaceHubspotFieldsOptions[] = []

export const MapCrmFields: React.FC<MapHubspotFieldsProps> = ({
  hubspotField,
  hubspotObject,
  hubspotFieldData = EMPTY_HUBSPOT_FIELD_DATA,
  category = '',
  fieldKey,
  picklistMappings = EMPTY_PICKLIST_MAPPINGS,
  openModal,
  setOpenModal,
  picklist = EMPTY_PICKLIST,
  updateAction,
}) => {
  const { classes } = useStyles()

  const tracker = useTracker()
  const user = useSelector<UserProfile | undefined>(
    state => state.userProfile.userProfile
  )
  const username = `${user?.givenName || ''} ${user?.familyName || ''}`.trim()
  const partnerType = useUserType()
  const crmId = useSelector<string>(
    state => state.AccountData[partnerType]?.partnerData?.crmId || ''
  )
  const customerData = customerFormData()
  const { crmType } = customerData

  const [isUnmappedFieldsModalOpen, setIsUnmappedFieldsModalOpen] =
    useState(false)
  const [fieldMappingData, setFieldMappingData] =
    useState<MarketplaceFieldMappingPicklistData[]>(picklistMappings)
  const [currentMappings, setCurrentMappings] =
    useState<MarketplaceFieldMappingPicklistData[]>(picklistMappings)
  const [remainingOptions, setRemainingOptions] = useState<
    MarketplaceHubspotFieldsOptions[]
  >([])
  const [selectedReferralValues, setSelectedReferralValues] = useState<
    string[]
  >([])
  const { value: canvasCosellImprovements } = useFlagValue(
    'canvasCosellImprovements'
  )
  const enforceCrmPicklistForMap = !!canvasCosellImprovements

  const isSelectedCrmPicklistField = useMemo(
    () =>
      resolveIsSelectedCrmPicklistField({
        hubspotField,
        hubspotObject,
        hubspotFieldData,
      }),
    [hubspotField, hubspotObject, hubspotFieldData]
  )

  const isPicklistMapModalOpen =
    openModal && (!enforceCrmPicklistForMap || isSelectedCrmPicklistField)

  // Sync local state with picklistMappings prop when modal is opened or picklistMappings changes
  useEffect(() => {
    if (isPicklistMapModalOpen) {
      setFieldMappingData(picklistMappings)
      setCurrentMappings(picklistMappings)
    }
  }, [isPicklistMapModalOpen, picklistMappings])

  // Refuse to keep the modal open unless CRM field is a valid picklist
  useEffect(() => {
    if (enforceCrmPicklistForMap && openModal && !isSelectedCrmPicklistField) {
      setOpenModal?.(false)
    }
  }, [
    enforceCrmPicklistForMap,
    openModal,
    isSelectedCrmPicklistField,
    setOpenModal,
  ])

  const trackEvent = ({
    action,
    title,
    section = 'Hubspot Fields Mapping',
    component,
    customData = {},
  }: {
    action: string
    title: string
    section?: string
    component?: string
    customData?: Record<string, any>
  }) => {
    const snakeCaseSectionName = snakeize(section)
    const snakeCaseComponentName = snakeize(component || 'MapHubspotFields')
    tracker?.track({
      section: `mapping_manager-${snakeCaseSectionName}`,
      component: snakeCaseComponentName,
      action,
      title,
      partnerId: `${crmId}`,
      customerName: `${username}`,
      ...customData,
    })
  }

  useEffect(() => {
    if (isPicklistMapModalOpen) {
      trackEvent({
        action: 'open-mapping-modal',
        title: 'Open Map Picklist Values Modal',
        customData: {
          hubspotField,
          hubspotObject,
          fieldKey,
          category,
        },
      })
    }
  }, [isPicklistMapModalOpen, hubspotField, hubspotObject, fieldKey, category])

  const allHubspotOptions = useMemo(() => {
    const fieldData = hubspotFieldData?.find(
      item =>
        item.internalName === hubspotField &&
        item.objectType === hubspotObject &&
        isCrmPicklistFieldType(item.fieldType)
    )
    return fieldData?.options || []
  }, [hubspotField, hubspotObject, hubspotFieldData])

  const allSelectedHubspotValues = useMemo(
    () => currentMappings.map(m => m.hubspotValue),
    [currentMappings]
  )

  const handleRowSelectionChange = useCallback(
    (
      newMappings: MarketplaceFieldMappingPicklistData[],
      referralValue: string
    ) => {
      setCurrentMappings(prev => [
        ...prev.filter(m => m.referralValue !== referralValue),
        ...newMappings,
      ])
      setFieldMappingData(prev => [
        ...prev.filter(m => m.referralValue !== referralValue),
        ...newMappings,
      ])
    },
    []
  )

  const handleSavePrimaryModal = useCallback(async () => {
    const mappedHubspotValues = new Set(
      fieldMappingData.map(item => item.hubspotValue)
    )
    const unmappedHubspotOptions = allHubspotOptions.filter(
      (option: any) => !mappedHubspotValues.has(option.value)
    )

    if (unmappedHubspotOptions.length > 0) {
      trackEvent({
        action: 'unmapped-fields-detected',
        title: 'Unmapped Hubspot Fields Detected',
        customData: {
          fieldKey,
        },
      })
      setRemainingOptions(unmappedHubspotOptions)
      setOpenModal?.(false)
      setIsUnmappedFieldsModalOpen(true)
      return
    }
    trackEvent({
      action: 'save-field-mappings',
      title: 'Save Field Mappings',
      customData: {
        fieldKey,
      },
    })
    if (updateAction) {
      updateAction(category, fieldKey, {
        picklistMappings: fieldMappingData,
      })
    }
    setOpenModal?.(false)
  }, [fieldMappingData, allHubspotOptions, setOpenModal, category, fieldKey])

  const handleSaveSecondaryModal = useCallback(async () => {
    if (selectedReferralValues.length === 0 || remainingOptions.length === 0)
      return

    trackEvent({
      action: 'save-remaining-mappings',
      title: 'Save Remaining Field Mappings',
      customData: {
        fieldKey,
      },
    })

    const newMappings = remainingOptions.flatMap(option =>
      selectedReferralValues.map(referralValue => ({
        referralValue,
        hubspotValue: option.value,
        hubspotLabel: option.label,
      }))
    )

    const updatedMappings = [...fieldMappingData, ...newMappings]
    setFieldMappingData(updatedMappings)
    setCurrentMappings(updatedMappings)

    if (updateAction) {
      updateAction(category, fieldKey, { picklistMappings: updatedMappings })
    }
    setIsUnmappedFieldsModalOpen(false)
    setSelectedReferralValues([])
  }, [
    selectedReferralValues,
    remainingOptions,
    fieldMappingData,
    category,
    fieldKey,
  ])

  const handleDeleteReferralValue = useCallback((value: string) => {
    setSelectedReferralValues(prev => prev.filter(v => v !== value))
  }, [])

  const handleClosePrimaryModal = useCallback(() => {
    setFieldMappingData(picklistMappings)
    setCurrentMappings(picklistMappings)
    setSelectedReferralValues([])
    setRemainingOptions([])
    setOpenModal?.(false)
  }, [picklistMappings, setOpenModal])

  const handleCloseSecondaryModal = useCallback(() => {
    setSelectedReferralValues([])
    setRemainingOptions([])
    setIsUnmappedFieldsModalOpen(false)
    setOpenModal?.(true)
  }, [setOpenModal])

  const tableData = useMemo(() => {
    return (picklist || []).map(value => ({
      id: value.value,
      referralPicklistValues: (
        <Typography className={classes.referralPicklistLabel}>
          {value.label}
        </Typography>
      ),
      opportunityValues: (
        <HubspotValuesMultiSelect
          key={`${value.value}-${currentMappings.length}`}
          picklistMappings={currentMappings.filter(
            m => m.referralValue === value.value
          )}
          referralValue={value.value}
          allOptions={allHubspotOptions}
          allSelectedValues={allSelectedHubspotValues}
          onSelectionChange={m => handleRowSelectionChange(m, value.value)}
        />
      ),
    }))
  }, [
    picklist,
    currentMappings,
    allHubspotOptions,
    allSelectedHubspotValues,
    handleRowSelectionChange,
  ])

  const getOpportunityValuesHeader = useCallback((key: string): string => {
    return `${camelCaseToWords(key)} values`
  }, [])

  const customHeaders = useMemo(
    () => ({
      fieldsToMap: (
        <MapCrmFieldsCustomHeaderCell
          label="Referral picklist values"
          className={classes.referalPicklistCell}
        />
      ),
      fieldType: (
        <MapCrmFieldsCustomHeaderCell
          label={getOpportunityValuesHeader(fieldKey)}
          className={classes.opportunityCell}
        />
      ),
    }),
    [classes, getOpportunityValuesHeader, fieldKey]
  )

  return (
    <>
      <ModalConfirmV2
        data-testid="cosell-map-hubspot-fields-modal"
        titleAcceptButton="Save"
        title="Map picklist values"
        icon={
          <FontAwesomeIcon icon={faLineColumns} className={classes.titleIcon} />
        }
        onCloseModal={handleClosePrimaryModal}
        onAccept={handleSavePrimaryModal}
        content={
          <div className={classes.contentContainer}>
            <TableComponentV2
              keyName={'map-hubspot-fields-table'}
              objects={tableData}
              mapping={MapTablePicklistMapping}
              customHeader={customHeaders}
              fetchMoreData={() => {}}
              selectedItem={[]}
              onChangeCheckbox={() => {}}
              showCheckbox={false}
              breakWorkHeader={false}
              tableHeight="calc(100vh - 250px)"
              hasMoreData={false}
              isBasicTable={true}
              borderBetweenRows={true}
            />
          </div>
        }
        acceptDisabled={false}
        hideAcceptButton={false}
        onlyCloseModal={true}
        open={isPicklistMapModalOpen}
        hideCancelButton={false}
        variant={ModalSizeVariants.large}
        onDecline={handleClosePrimaryModal}
        scrollContent={true}
      />
      <ModalConfirmV2
        data-testid="cosell-map-unmapped-hubspot-fields-modal"
        titleAcceptButton="Save"
        title="Map remaining picklist values"
        icon={
          <FontAwesomeIcon icon={faLineColumns} className={classes.titleIcon} />
        }
        onCloseModal={handleCloseSecondaryModal}
        onAccept={handleSaveSecondaryModal}
        content={
          <div className={classes.contentContainer}>
            <div className={classes.descriptionContent}>
              Choose referral field values to map to remaining{' '}
              {crmType === CRMTypes.salesforce ? 'Salesforce' : 'Hubspot'}{' '}
              options.
            </div>
            <div className={classes.nestedSelect}>
              <MultiSelectV4
                field={{
                  title: 'Select referral values',
                  placeHolder: 'Select referral values',
                  humanReadableKey: 'referralValues',
                  id: 'referralValues',
                  isRequired: true,
                  isReadOnly: false,
                  isCustom: false,
                  dataType: 'multi_enum',
                  options: (picklist || []).map(item => ({
                    value: item.value,
                    label: item.label,
                  })),
                }}
                onChangeSelect={(e: any) =>
                  setSelectedReferralValues(e.target.value)
                }
                selectedValues={selectedReferralValues}
                handleDelete={handleDeleteReferralValue}
                chipDisplayLimit={4}
              />
            </div>
          </div>
        }
        acceptDisabled={selectedReferralValues.length === 0}
        hideAcceptButton={false}
        onlyCloseModal={true}
        open={isUnmappedFieldsModalOpen}
        hideCancelButton={false}
        variant={ModalSizeVariants.large}
        onDecline={handleCloseSecondaryModal}
        scrollContent={false}
      />
    </>
  )
}
